import { Injectable, Injector } from '@angular/core';
import { Utils } from 'weavix-shared/utils/utils';
import { BehaviorSubject, filter, firstValueFrom, map, of, timeout } from 'rxjs';
import { WebMqttStorage } from './web-mqtt-storage';
import { environment } from '../../environments/environment';
import { Topic } from '@weavix/models/src/topic/topic';
import { HttpService } from './http.service';
import { MqttService, mqttEndpoint } from '@weavix/mqtt/src/mqtt-service';
import { mqttTopicReplace } from '@weavix/mqtt/src/utils/mqtt-topic-replace';
import { MqttQos } from '@weavix/mqtt/src/models/mqtt-qos';
import { MqttTransformationType } from '@weavix/mqtt/src/models/mqtt-transformation-type';
import { ObservableSubscribe } from '@weavix/mqtt/src/models/observable-subscribe';
import { TopicPayload } from '@weavix/mqtt/src/models/topic-payload';
import { subscribeWithReplay as mqttSubscribeWithReplay } from '@weavix/mqtt/src/utils/subscribe-with-replay';
import { subscribeWithReconnect } from '@weavix/mqtt/src/utils/subscribe-with-reconnect';
import { TelemetryEvent, TelemetryEventPayloads } from '@weavix/telemetry';
import { SyncService } from '@weavix/sync/src/sync.service';
import { TelemetryService } from './telemetry.service';
import { catchUpSyncAndReport } from '@weavix/mqtt/src/utils/sync-catch-up';
import { EMQX_CONNECT_FALLBACK_MS, MqttBrokerSelector, MqttBrokerTarget, MqttEndpoint } from './mqtt-broker-selector';
// shared worker connection
// import { getWorker } from '../workers/shared.client';

@Injectable({
    providedIn: 'root',
})
export class PubSubService {
    private internalClient: MqttService;
    private readonly storage = new WebMqttStorage('outgoing');
    private options: any = {};
    private connecting = false;
    /** Counts sign-outs, so a sign-in still waiting on its broker check when the user signs out does not connect. */
    private signOuts = 0;
    /**
     * Numbers each broker check, so every check is its own request: HttpService shares a pending GET to the same URL,
     * which would hand a retry the request that timed out, or a new sign-in an earlier session's answer. */
    private brokerChecks = 0;
    /** The sign-in in progress; each waits for the one before, so a later one connects or moves from where it left off. */
    private signingIn: Promise<void> = Promise.resolve();
    /** The Teams bearer token the current client signed in with; a new one is a new sign-in and needs a new client. */
    private clientBearer = '';
    // test tokens
    static token = '';

    connected$ = new BehaviorSubject<boolean>(false);

    /** Chooses Aedes or EMQX for this browser; see {@link MqttBrokerSelector}. */
    readonly broker: MqttBrokerSelector;
    /** True while a PTT is being sent or played; set by PttService, which depends on this service. */
    private pttBusy: () => boolean = () => false;

    constructor(private readonly injector: Injector) {
        this.broker = new MqttBrokerSelector({
            fetchTarget: () => this.injector.get(HttpService).get<MqttBrokerTarget>(null, '/core/mqtt/connection', { check: ++this.brokerChecks }),
            aedes: () => this.aedesEndpoint(),
            bearerToken: () => (environment.teamsApp && HttpService.bearerToken) || '',
            apply: (endpoint, reconnect) => this.useEndpoint(endpoint, reconnect),
            isOnEndpoint: endpoint => !!this.internalClient?.connected$.value && this.internalClient.endpoint === mqttEndpoint(endpoint.url, endpoint.port),
            isPttBusy: () => this.pttBusy(),
            catchUpSync: (movedAt, endpoint) => this.catchUpSync(movedAt, endpoint),
            report: event => this.sendTelemetry(TelemetryEvent.MqttBrokerSwitched, event),
        });
        this.initInternalClient();
    }

    /** Lets PttService hold off a broker switch while a PTT is in progress. */
    setPttBusyCheck(isBusy: () => boolean) {
        this.pttBusy = isBusy;
    }

    get clientId() {
        return this.internalClient.clientId;
    }

    async disconnect() {
        this.signOuts++;
        this.broker.stop();
        const oldClient = this.internalClient;
        this.internalClient = null;
        this.connecting = false;
        oldClient?.disconnect();
    }

    /** Connects for a sign-in, after any sign-in still in progress; a sign-out before it runs or while it waits cancels it. */
    loggedIn(): Promise<void> {
        const signOuts = this.signOuts;
        const run = this.signingIn.then(() => this.signIn(signOuts));
        this.signingIn = run.catch(() => undefined);
        return run;
    }

    private async signIn(signOuts: number) {
        if (signOuts !== this.signOuts) return;
        await this.broker.start(!this.connecting);
        if (signOuts !== this.signOuts) return;
        const oldClient = this.internalClient;
        if (!this.initInternalClient()) {
            if (!this.connecting) {
                this.internalClient.connect();
                this.connecting = true;
            }
            return;
        }
        this.internalClient.connect();
        this.connecting = true;
        oldClient?.disconnect();
    }

    /** Aedes, as configured for this app; the cookie signs in there, so the token is empty except for Teams and test tokens. */
    private aedesEndpoint(): MqttEndpoint {
        return {
            url: environment.pubsubApi,
            port: environment.pubsubPort,
            token: environment.teamsApp && HttpService.bearerToken ? HttpService.bearerToken : PubSubService.token,
            ssl: !!environment.is360Api.includes('https://'),
        };
    }

    /**
     * Points the client at `endpoint`. Options are updated in place, which MqttService reads on its next connection, so
     * a renewed token needs no reconnect; a move to another broker reconnects. */
    private useEndpoint(endpoint: MqttEndpoint, reconnect: boolean) {
        Object.assign(this.options, endpoint);
        if (reconnect && this.connecting) this.internalClient?.reconnect();
    }

    /**
     * Catches sync up once the client has reached the broker it moved to, as mobile does: messages sent during the move
     * are not redelivered. Reported as `SyncCatchUp`. */
    private async catchUpSync(movedAt: number, endpoint: MqttEndpoint) {
        const client = this.internalClient;
        const target = mqttEndpoint(endpoint.url, endpoint.port);
        const reached = !!client && await firstValueFrom(client.connected$.pipe(
            filter(connected => connected && client.endpoint === target),
            map(() => true),
            timeout({ first: EMQX_CONNECT_FALLBACK_MS, with: () => of(false) }),
        ));
        await catchUpSyncAndReport(
            at => SyncService.instance?.catchUp(at) ?? Promise.resolve({ partitions: 0, failed: 0 }),
            payload => this.sendTelemetry(TelemetryEvent.SyncCatchUp, { ...payload }),
            movedAt,
            reached,
        );
    }

    /** Sends a telemetry event, resolving TelemetryService at send time since it depends on this service. */
    private sendTelemetry<E extends TelemetryEvent>(name: E, data: TelemetryEventPayloads[E]) {
        try {
            this.injector.get(TelemetryService).sendTelemetry(name, data).catch(e => console.error('PubSubService: telemetry failed', e));
        } catch (e) {
            console.error('PubSubService: telemetry unavailable', e);
        }
    }

    /**
     * Creates the client for the current endpoint and token, or for a new Teams sign-in; false when the existing one
     * already matches. An MQTT token renewed in place matches, since the options already carry it. */
    private initInternalClient() {
        const platform = environment.teamsApp ? 'teams' : environment.radioApp ? 'web-radio' : 'console';
        const options = {
            // connect: getWorker().connect as any,
            platform,
            source: `${platform}-${environment.version}`,
            storage: this.storage,
            timeout: 30,
            ...(this.broker.emqx ?? this.aedesEndpoint()),
        };
        const bearer = environment.teamsApp ? HttpService.bearerToken || '' : '';
        if (this.internalClient && bearer === this.clientBearer && Object.keys(options).every(key => options[key] === this.options[key])) return false;
        this.clientBearer = bearer;
        this.options = options;
        this.internalClient = new MqttService(options);
        this.internalClient.connected$.subscribe(val => {
            this.broker.onConnected(val);
            if (val !== this.connected$.value) this.connected$.next(val);
        });
        return true;
    }

    public async publish(topic: Topic, args: string[], payload: any, qos: MqttQos = MqttQos.atLeastOnce) {
        const resolvedTopic = mqttTopicReplace(topic, args);

        const blob = payload instanceof Uint8Array ? payload : payload instanceof Blob ? await (payload as any).arrayBuffer() : JSON.stringify(payload);

        this.internalClient.publishRaw(resolvedTopic, blob, qos);
    }

    public subscribe<T>(component: any, topic: Topic, args: string[], retain: boolean | number = true, qos: MqttQos = MqttQos.atLeastOnce, raw: boolean = false): ObservableSubscribe<TopicPayload<T>> {
        const resolvedTopic = mqttTopicReplace(topic, args);
        const transform = raw ? MqttTransformationType.raw : MqttTransformationType.json;

        const obs = this.subscribeInternal<T>(resolvedTopic, retain, qos, transform);

        const safeObs = Utils.safeSubscribe(component, obs);
        return Object.assign(safeObs, { subscribed: obs.subscribed });
    }

    // pass through methods must throw errors when disconnected whereas normal subscribe methods simply reconnect
    public subscribePassThrough<T>(topic: string): ObservableSubscribe<TopicPayload<T>> {
        return this.internalClient.subscribe(topic);
    }

    private subscribeInternal<T>(resolvedTopic: string, retain: boolean | number, qos: MqttQos, transform: MqttTransformationType): ObservableSubscribe<TopicPayload<T>> {
        if (!retain) {
            return subscribeWithReconnect<T>(this.internalClient, resolvedTopic, qos as any, transform);
        } else {
            const bufferSize = (retain === true || retain === 1) ? 1 : undefined; // undefined === infinite
            return this.subscribeWithReplay(resolvedTopic, bufferSize, qos, transform);
        }
    }

    private subscribeWithReplay<T>(topicString: string, bufferSize: number | undefined, qos: MqttQos, transform: MqttTransformationType): ObservableSubscribe<TopicPayload<T>> {
        return mqttSubscribeWithReplay(topicString, bufferSize, () => {
            return subscribeWithReconnect(this.internalClient, topicString, qos, transform);
        });
    }
}
